/**
 * What: Provides browser-local report notifications over the shared Shopify StudyRun activity query.
 * Transport & data-flow: React Query owns minimal status transport and cadence; this provider persists run IDs and routes report actions.
 *
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import { useQueryClient } from '@tanstack/react-query'
import type { VerifiedTestFeedConnectionScope } from './ShopifyAppContext'
import type { shopifyApiFetch } from './shopifyApiFetch'
import { advanceNotificationState, uniqueIds, type NotificationState } from './studyActivityNotifications'
import { useShopifyStudyActivityQuery } from './hooks/useShopifyStudyActivityQuery'

type ShopifyStudyActivityContextValue = {
  activityError: string | null
  isActivityFetching: boolean
  unreadRunIds: string[]
  trackPendingRun: (runId: string, studyId: string) => void
  markReportRead: (runId: string) => void
  refreshStudyActivity: () => Promise<void>
  openReport: (runId: string) => void
}

type ShopifyStudyActivityProviderProps = {
  children: ReactNode
  shopify: Parameters<typeof shopifyApiFetch>[0]
  scope: VerifiedTestFeedConnectionScope | null
  onOpenResults?: (studyId: string, runId: string) => void
}

const EMPTY_NOTIFICATION_STATE: NotificationState = {
  pendingRunIds: [],
  unreadRunIds: [],
  seenRunIds: [],
  studyIdByRunId: {},
}

const ShopifyStudyActivityContext = createContext<ShopifyStudyActivityContextValue | null>(null)

function getNotificationStorage() {
  if (typeof window !== 'undefined') return globalThis.window.localStorage
  return null
}

function readStudyIdByRunId(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return Object.fromEntries(
    Object.entries(value).flatMap(([runId, studyId]) =>
      typeof studyId === 'string' && runId.trim() && studyId.trim() ? [[runId, studyId]] : [],
    ),
  )
}

function readNotificationState(storageKey: string | null): NotificationState {
  if (!storageKey) return EMPTY_NOTIFICATION_STATE
  try {
    const storage = getNotificationStorage()
    if (!storage) return EMPTY_NOTIFICATION_STATE
    const parsed = JSON.parse(storage.getItem(storageKey) ?? '{}') as Partial<NotificationState>
    return {
      pendingRunIds: uniqueIds(parsed.pendingRunIds),
      unreadRunIds: uniqueIds(parsed.unreadRunIds),
      seenRunIds: uniqueIds(parsed.seenRunIds),
      studyIdByRunId: readStudyIdByRunId(parsed.studyIdByRunId),
    }
  } catch (error) {
    console.error('Unable to read Shopify study activity notifications', error)
    return EMPTY_NOTIFICATION_STATE
  }
}

function writeNotificationState(storageKey: string | null, state: NotificationState) {
  if (!storageKey) return
  try {
    const storage = getNotificationStorage()
    if (!storage) return
    storage.setItem(storageKey, JSON.stringify(state))
  } catch (error) {
    console.error('Unable to persist Shopify study activity notifications', error)
  }
}

function buildStorageKey(scope: VerifiedTestFeedConnectionScope | null) {
  if (!scope) return null
  return `testfeed:shopify-study-activity:v2:${scope.shopDomain}:${scope.connectionId}`
}

function notificationStatesMatch(left: NotificationState, right: NotificationState) {
  return JSON.stringify(left) === JSON.stringify(right)
}

/** Supplies shared status and notification state to every Shopify study surface. */
export function ShopifyStudyActivityProvider({
  children,
  shopify,
  scope,
  onOpenResults,
}: ShopifyStudyActivityProviderProps) {
  const appBridge = useAppBridge()
  const storageKey = buildStorageKey(scope)
  const [notificationState, setNotificationState] = useState(() => ({
    storageKey,
    value: readNotificationState(storageKey),
  }))
  const notifications = notificationState.value
  const notificationsRef = useRef(notifications)
  const activeStorageKeyRef = useRef(storageKey)
  const queryClient = useQueryClient()
  const pendingStudies = Object.fromEntries(
    notifications.pendingRunIds.flatMap((runId) => {
      const studyId = notificationState.storageKey === storageKey ? notifications.studyIdByRunId[runId] : undefined
      return studyId ? [[runId, studyId]] : []
    }),
  )
  const activityQuery = useShopifyStudyActivityQuery({ shopify, scope, studyIdByRunId: pendingStudies })
  useEffect(() => {
    notificationsRef.current = notifications
  }, [notifications])
  useEffect(() => {
    if (activeStorageKeyRef.current === storageKey) return
    activeStorageKeyRef.current = storageKey
    const next = readNotificationState(storageKey)
    notificationsRef.current = next
    setNotificationState({ storageKey, value: next })
  }, [storageKey])

  const applyNotificationState = useCallback(
    (next: NotificationState) => {
      notificationsRef.current = next
      writeNotificationState(storageKey, next)
      setNotificationState({ storageKey, value: next })
    },
    [storageKey],
  )

  const openReport = useCallback(
    (runId: string) => {
      const studyId = notificationsRef.current.studyIdByRunId[runId]
      if (studyId) onOpenResults?.(studyId, runId)
    },
    [onOpenResults],
  )

  const promoteReadyRuns = useCallback(
    (current: NotificationState) => {
      if (activeStorageKeyRef.current !== storageKey) return current
      const { next, newlyReadyRunIds } = advanceNotificationState(current, activityQuery.snapshot)
      if (!notificationStatesMatch(current, next)) applyNotificationState(next)
      for (const runId of newlyReadyRunIds) {
        appBridge.toast?.show('Your test results are ready', {
          action: 'View results',
          onAction: () => openReport(runId),
        })
      }
      return next
    },
    [activityQuery.snapshot, appBridge, applyNotificationState, openReport, storageKey],
  )

  useEffect(() => {
    promoteReadyRuns(notifications)
  }, [notifications, promoteReadyRuns])

  const trackPendingRun = useCallback(
    (runId: string, studyId: string) => {
      const normalizedRunId = runId.trim()
      const normalizedStudyId = studyId.trim()
      if (!normalizedRunId || !normalizedStudyId) return
      const current = notificationsRef.current
      const next = {
        ...current,
        pendingRunIds: current.pendingRunIds.includes(normalizedRunId)
          ? current.pendingRunIds
          : [...current.pendingRunIds, normalizedRunId],
        studyIdByRunId: { ...current.studyIdByRunId, [normalizedRunId]: normalizedStudyId },
      }
      applyNotificationState(next)
      void queryClient.invalidateQueries({ queryKey: activityQuery.queryKey })
    },
    [activityQuery.queryKey, applyNotificationState, queryClient],
  )

  const markReportRead = useCallback(
    (runId: string) => {
      const { [runId]: _readStudyId, ...studyIdByRunId } = notificationsRef.current.studyIdByRunId
      const next = {
        ...notificationsRef.current,
        pendingRunIds: notificationsRef.current.pendingRunIds.filter((id) => id !== runId),
        unreadRunIds: notificationsRef.current.unreadRunIds.filter((id) => id !== runId),
        studyIdByRunId,
      }
      applyNotificationState(next)
    },
    [applyNotificationState],
  )

  const refreshStudyActivity = useCallback(async () => {
    await activityQuery.refetch({ cancelRefetch: false })
  }, [activityQuery.refetch])

  const value = useMemo<ShopifyStudyActivityContextValue>(
    () => ({
      activityError:
        Object.keys(activityQuery.snapshot.errorsByRunId).length > 0
          ? 'Test results could not be checked. Try again.'
          : null,
      isActivityFetching: activityQuery.isFetching,
      unreadRunIds: notifications.unreadRunIds,
      trackPendingRun,
      markReportRead,
      refreshStudyActivity,
      openReport,
    }),
    [
      markReportRead,
      notifications.unreadRunIds,
      openReport,
      refreshStudyActivity,
      activityQuery.isFetching,
      activityQuery.snapshot.errorsByRunId,
      trackPendingRun,
    ],
  )

  return <ShopifyStudyActivityContext.Provider value={value}>{children}</ShopifyStudyActivityContext.Provider>
}

/** Safely consumes shared Shopify StudyRun activity state. */
export function useShopifyStudyActivity() {
  const context = useContext(ShopifyStudyActivityContext)
  if (!context) throw new Error('useShopifyStudyActivity must be used within ShopifyStudyActivityProvider')
  return context
}
